import React, { useState } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import {
  FileUploadPreSignedUrlInput,
  FileUploadPreSignedUrlResponse,
  PgeStoreFileUrlRequest,
  PgeStoreFileUrlResponse,
  QueryGetFileUploadPreSignedUrlArgs,
  QueryGetPgeStoreFileUrlArgs,
  UploadStoreRequest,
  UploadStoreResponse,
} from '../../__generated__/pge-types';
import { Maybe } from '../../__generated__/pgeplus-types';
import gql from 'not-graphql-tag';
import useUtil from './useUtil';
import { useLocation } from '@reach/router';

export const getPresignedURLQuery = gql`
  query GetPreSignedUrl($param: FileUploadPreSignedUrlInput!) {
    getPreSignedUrl(param: $param) {
      key
      uploadUrl
    }
  }
`;

export const uploadStoreMutation = gql`
  mutation UploadStore($payload: UploadStoreRequest) {
    uploadStore(payload: $payload) {
      success
      pgeStorekeys {
        pgeStoreKey
        tempKey
      }
    }
  }
`;

export const getPgeStoreFileURLQuery = gql`
  query GetPgeStoreFileUrl($param: PgeStoreFileUrlRequest!) {
    getPgeStoreFileUrl(param: $param) {
      downloadUrl
      key
    }
  }
`;

type UploadFileResponse = {
  success: boolean;
  pgeStoreKey: Maybe<string>;
  pgeTempStoreKey: Maybe<string>;
};

const useFileUploadApi = () => {
  const apolloClient = useApolloClient();
  const [loading, setLoading] = useState<boolean>(false);
  const { setErrorNotification } = useUtil();
  const { pathname } = useLocation();
  const isInstallerPortal = pathname.startsWith('/installer/');
  const getFileuploadURL = async (param: FileUploadPreSignedUrlInput) => {
    const result = await apolloClient.query<
      { getPreSignedUrl: FileUploadPreSignedUrlResponse },
      QueryGetFileUploadPreSignedUrlArgs
    >({
      query: getPresignedURLQuery,
      variables: {
        param,
      },
      context: isInstallerPortal
        ? {
            installerPortal: true,
          }
        : {},
      fetchPolicy: 'network-only',
    });

    return result?.data?.getPreSignedUrl;
  };

  const uploadFileToS3 = async (file: File, uploadUrl: string) => {
    //Uploading file to s3
    return fetch(uploadUrl, {
      method: 'PUT',
      body: file,
    });
  };

  const uploadToPGEStore = async (keys: string[]) => {
    const response = await apolloClient.mutate<
      {
        uploadStore: UploadStoreResponse;
      },
      { payload: UploadStoreRequest }
    >({
      mutation: uploadStoreMutation,
      variables: {
        payload: {
          addKeys: keys,
          delKeys: [],
          featureType: 'EVInstall',
          description: 'EV install',
        },
      },
      context: isInstallerPortal
        ? {
            installerPortal: true,
          }
        : {},
      errorPolicy: 'all',
    });

    return response.data?.uploadStore;
  };

  const uploadFile = async (file: File): Promise<UploadFileResponse> => {
    try {
      setLoading(true);

      //Get pre-signed URL for uploading the image to S3
      const fileploadURLResponse = await getFileuploadURL({
        key: file.name,
      });

      //Uploading file to S3
      const uploadResponse = await uploadFileToS3(
        file,
        fileploadURLResponse.uploadUrl,
      );

      //Move the image from temp s3 bucket to pgeStore bucket
      const response = await uploadToPGEStore([fileploadURLResponse.key]);

      if (response?.success) {
        return {
          success: true,
          pgeStoreKey: response.pgeStorekeys?.[0]?.pgeStoreKey!,
          pgeTempStoreKey: fileploadURLResponse?.key,
        };
      } else {
        return {
          success: false,
          pgeStoreKey: null,
          pgeTempStoreKey: null,
        };
      }
    } catch (e) {
      setErrorNotification(true);
      return {
        success: false,
        pgeStoreKey: null,
        pgeTempStoreKey: null,
      };
    } finally {
      setLoading(false);
    }
  };

  const getPgeStoreDownloadUrl = async (param: PgeStoreFileUrlRequest) => {
    const result = await apolloClient.query<
      { getPgeStoreFileUrl: PgeStoreFileUrlResponse[] },
      QueryGetPgeStoreFileUrlArgs
    >({
      query: getPgeStoreFileURLQuery,
      variables: {
        param,
      },
      fetchPolicy: 'network-only',
      context: isInstallerPortal
        ? {
            installerPortal: true,
          }
        : {},
    });

    return result?.data?.getPgeStoreFileUrl;
  };

  return {
    loading,
    uploadFile,
    getPgeStoreDownloadUrl,
  };
};

export default useFileUploadApi;
